<script setup lang="ts">
import { Mask } from 'maska';
import type { UserDocument } from '@/types/UserData';

const DEFAULT_CPF_MASK: string = '###.###.###-##';

type Props = {
  name?: string;
  hideIcon?: boolean;
  antiRobotToken: string;
  hasError?: boolean;
};

const {
  name = 'cpf',
  hideIcon,
  antiRobotToken,
  hasError = false,
} = defineProps<Props>();

type Emits = {
  'validate-document': [UserDocument];
  'remove-document': [];
  'reset-anti-robot-token': [];
};

const emit = defineEmits<Emits>();

const maskedModel = defineModel<string>('masked', {
  default: '',
});

const { t } = useTexts();
const authStore = useAuthStore();
const { openToaster } = useLayoutStore();
const { isCaptchaValidateCpfEnabled } = useCaptcha();

const isLoading = ref<boolean>(false);
const isDocumentValid = ref<boolean>(false);
const userDocumentError = ref<string>('');
const cpfMask = ref<string>(DEFAULT_CPF_MASK);

const unmaskedCpf = computed<string>(() => {
  const mask = new Mask({ mask: cpfMask.value });

  return mask.unmasked(maskedModel.value);
});

const isIconVisible = computed<boolean>(() => {
  return !hideIcon;
});

const isChangeDocButtonVisible = computed<boolean>(() => {
  return !!maskedModel.value && isDocumentValid.value;
});

async function validateDocument() {
  if (!antiRobotToken && isCaptchaValidateCpfEnabled.value) {
    openToaster({
      message: t('auth.login.verify_captcha'),
      type: 'warning',
    });

    return;
  }

  isLoading.value = true;

  type Payload = {
    number: string;
    captcha_token: string;
  };

  const payload: Payload = {
    number: unmaskedCpf.value,
    captcha_token: antiRobotToken,
  };

  await authStore
    .verifyDocument(payload)
    .then((res) => {
      if (res?.success) {
        isDocumentValid.value = true;
        userDocumentError.value = '';

        emit('validate-document', res.data);

        return;
      }

      const message = res?.message
        ? t(res?.message)
        : t('deposit.errors.document');

      const [errorsProviders] = res?.errors_providers || [];

      const errorFromDataset = errorsProviders
        ? Object.values(errorsProviders || {}).map(
            (data: any) => data[0].message,
          )
        : [];

      userDocumentError.value = errorFromDataset.length
        ? errorFromDataset[0]
        : message;

      openToaster({
        message: userDocumentError.value,
        type: 'error',
      });
    })
    .catch((err) => {
      isDocumentValid.value = false;

      const message = err.message
        ? t(err.message)
        : t('deposit.errors.document');

      openToaster({
        message,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;

      emit('reset-anti-robot-token');
    });
}

const isLoadingCaptchaTokenVisible = computed<boolean>(() => {
  return (
    isCaptchaValidateCpfEnabled.value &&
    !antiRobotToken &&
    !isDocumentValid.value
  );
});

function removeDocument() {
  isDocumentValid.value = false;
  maskedModel.value = '';

  emit('remove-document');
  emit('reset-anti-robot-token');
}

const isCpfValid = ref(false);

function handleValidateDocument() {
  if (isLoading.value) {
    openToaster({
      message: t('general.errors.please_wait'),
      type: 'warning',
    });

    return;
  }

  userDocumentError.value = '';
  isCpfValid.value = false;

  if (unmaskedCpf.value.length !== 11) {
    return;
  }

  isCpfValid.value = validateCpf(unmaskedCpf.value);

  if (isCpfValid.value) {
    validateDocument();
  } else {
    userDocumentError.value = t('auth.register.error.cpf');
  }
}

watch(unmaskedCpf, () => {
  if (unmaskedCpf.value.length >= 11) {
    if (!isCaptchaValidateCpfEnabled.value) {
      handleValidateDocument();
    } else if (antiRobotToken) {
      handleValidateDocument();
    }
  }
});

watch(userDocumentError, () => {
  if (!userDocumentError.value) {
    return;
  }

  setTimeout(() => {
    userDocumentError.value = '';
  }, 25000);
});
</script>

<template>
  <div :class="$style.inputCpf">
    <BaseFormInput
      id="document"
      v-model="maskedModel"
      class="flex-1 min-w-0"
      :class="[$style.input, { error: userDocumentError }]"
      :data-maska="cpfMask"
      v-bind="$attrs"
      :disabled="false"
      :has-error="hasError"
      :name="name"
      :readonly="isLoading || isDocumentValid"
      type="tel"
    >
      <template
        v-if="isIconVisible"
        #prepend
      >
        <div :class="$style.icon">
          <NuxtIcon
            v-if="maskedModel && isDocumentValid"
            class="text-success"
            name="check"
          />
          <NuxtIcon
            v-else
            name="file"
          />
        </div>
      </template>
    </BaseFormInput>

    <div :class="$style.append">
      <template v-if="isLoading || isLoadingCaptchaTokenVisible">
        <BaseLoading
          class="relative mr-4 bg-transparent"
          show
          size="small"
        />
      </template>

      <template v-else-if="isChangeDocButtonVisible">
        <button
          :class="$style.removeButton"
          @click.prevent="removeDocument"
        >
          {{ $t('account.user_data.change') }}
        </button>
      </template>
    </div>

    <div
      v-if="userDocumentError"
      :class="$style.errorMessage"
    >
      {{ userDocumentError }}
    </div>
  </div>
</template>

<style module lang="postcss">
.inputCpf {
  @apply relative flex items-center w-full;
}

.input {
  @apply flex-1 relative;

  &.small {
    @apply h-[40px] text-sm text-left pl-[40px];
  }

  &[disabled]:not([disabled='false']) {
    @apply opacity-40 cursor-not-allowed;
  }

  &[readonly]:not([readonly='false']) {
    @apply cursor-default;
  }
}

.icon {
  @apply w-9 h-9 text-sm text-auth-text-inputs/60 z-10 flex justify-center items-center;
}

.append {
  @apply relative h-full grid place-items-center ml-2;
}

.removeButton {
  @apply text-[.6rem] text-auth-text-inputs flex justify-center items-center rounded px-2 h-5 text-nowrap flex-nowrap bg-auth-text-inputs/20 whitespace-nowrap;
}

.errorMessage {
  @apply absolute bottom-0 right-0 rounded-br-md text-center text-[.5rem] pr-1 pl-1.5 pt-1 pb-0.5 bg-error text-texts;
}
</style>
